import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IntlShape } from "react-intl";
import { useNavigate } from "react-router";
import { AbstractTile } from "@/components/design/AbstractTile";
import { PowerCollage } from "@/components/grow/PowerCollage";
import { FilterChip } from "@/components/design/FilterChip";
import { BackButton } from "@/components/common/BackButton";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";

// Grow · Play (workshop snapshot screens/GrowPlay.jsx).
// Static catalogue of solo/team games – all design-spec placeholders for
// now. Cards with a `target` route navigate; the rest are non-playable.

type TileVariant = "dots" | "lines" | "grid" | "rings" | "block";

interface GameItem {
  id: string;
  target?: string;
  kindId: string;
  kindDefault: string;
  title: string;
  captionId: string;
  captionDefault: string;
  durationId: string;
  durationDefault: string;
  players: string;
  hue: number;
  variant: TileVariant;
  featured?: boolean;
  // Replaces the AbstractTile with a single blurred artwork as an ambient teaser.
  imageSrc?: string;
  // Replaces the AbstractTile with the 30-power PowerCollage. Each tile uncovers
  // only when its self-assessment is complete (not when the quiz is answered).
  collage?: boolean;
}

const GAMES: readonly GameItem[] = [
  {
    id: "power-quiz",
    kindId: "grow.play.game.powerQuiz.kind",
    kindDefault: "Quiz",
    title: "Power Quiz",
    captionId: "grow.play.game.powerQuiz.caption",
    captionDefault: "90 statements → your power profile. 30 categories, 5-point scale.",
    durationId: "grow.play.game.powerQuiz.duration",
    durationDefault: "20-30 min · solo",
    players: "1",
    hue: 140,
    variant: "grid",
    featured: true,
    target: "/grow/play/power-quiz",
    collage: true,
  },
  {
    id: "back-2-future",
    kindId: "grow.play.game.back2Future.kind",
    kindDefault: "Scenario",
    title: "Back2Future",
    captionId: "grow.play.game.back2Future.caption",
    captionDefault: "You throw your present self into a 10-year future. What do you see there?",
    durationId: "grow.play.game.back2Future.duration",
    durationDefault: "15 min · solo",
    players: "1",
    hue: 215,
    variant: "rings",
  },
  {
    id: "treasure-hunt",
    kindId: "grow.play.game.treasureHunt.kind",
    kindDefault: "Adventure",
    title: "Treasure Hunt",
    captionId: "grow.play.game.treasureHunt.caption",
    captionDefault: "Treasure map of your strengths: find 5 unrecognized powers in your everyday life.",
    durationId: "grow.play.game.treasureHunt.duration",
    durationDefault: "20 min · solo",
    players: "1",
    hue: 95,
    variant: "dots",
  },
  {
    id: "creative-jam",
    kindId: "grow.play.game.creativeJam.kind",
    kindDefault: "Team improv",
    title: "Creative Jam Session",
    captionId: "grow.play.game.creativeJam.caption",
    captionDefault: "Improvisation setting for 3–6 people. Fast, loud, honest.",
    durationId: "grow.play.game.creativeJam.duration",
    durationDefault: "30 min · team",
    players: "3–6",
    hue: 25,
    variant: "lines",
  },
  {
    id: "value-cards",
    kindId: "grow.play.game.valueCards.kind",
    kindDefault: "Card game",
    title: "Value Cards",
    captionId: "grow.play.game.valueCards.caption",
    captionDefault: "60 value cards: prioritize, eliminate, commit. With reflection questions.",
    durationId: "grow.play.game.valueCards.duration",
    durationDefault: "25 min · solo",
    players: "1",
    hue: 320,
    variant: "rings",
  },
  {
    id: "perspective-swap",
    kindId: "grow.play.game.perspectiveSwap.kind",
    kindDefault: "Role",
    title: "Perspective Swap",
    captionId: "grow.play.game.perspectiveSwap.caption",
    captionDefault: "Slip into another role for 10 minutes and see yourself from the outside.",
    durationId: "grow.play.game.perspectiveSwap.duration",
    durationDefault: "10 min · solo / duo",
    players: "1–2",
    hue: 200,
    variant: "lines",
  },
  {
    id: "mission-builder",
    kindId: "grow.play.game.missionBuilder.kind",
    kindDefault: "Story mode",
    title: "Mission Builder",
    captionId: "grow.play.game.missionBuilder.caption",
    captionDefault: "Create your personal hero's journey: calling, obstacles, allies.",
    durationId: "grow.play.game.missionBuilder.duration",
    durationDefault: "45 min · solo",
    players: "1",
    hue: 12,
    variant: "dots",
  },
  {
    id: "team-puzzle",
    kindId: "grow.play.game.teamPuzzle.kind",
    kindDefault: "Co-op",
    title: "Team Puzzle",
    captionId: "grow.play.game.teamPuzzle.caption",
    captionDefault: "Three people, three powers, one problem. Who fits together, and why?",
    durationId: "grow.play.game.teamPuzzle.duration",
    durationDefault: "20 min · team",
    players: "3",
    hue: 145,
    variant: "grid",
  },
];

type PlayFilter = "Alle" | "Solo" | "Team";

const FILTERS: readonly PlayFilter[] = ["Alle", "Solo", "Team"];

const FILTER_LABELS: Record<PlayFilter, { id: string; defaultMessage: string }> = {
  Alle: { id: "grow.play.filter.all", defaultMessage: "All" },
  Solo: { id: "grow.play.filter.solo", defaultMessage: "Solo" },
  Team: { id: "grow.play.filter.team", defaultMessage: "Team" },
};

export function GrowPlayPage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const [filter, setFilter] = useState<PlayFilter>("Alle");

  const filtered = GAMES.filter((g) => {
    if (filter === "Alle") return true;
    if (filter === "Solo") return g.players === "1";
    if (filter === "Team") return g.players !== "1";
    return true;
  });

  const featured = GAMES.find((g) => g.featured);
  const rest = filtered.filter((g) => !g.featured);

  // Live assistant context (motive 1, ADR 0020): the games arcade is static content, so this
  // describes what's on screen (the games) so "describe what you see" is answerable.
  useAssistantContext([
    {
      description: "games shown on the Play page",
      value:
        `featured: ${featured?.title} (${featured?.captionDefault}); ` +
        `others: ${GAMES.filter((g) => !g.featured).map((g) => g.title).join(", ")}` +
        (filter !== "Alle" ? `; filtered to ${filter}` : ""),
    },
  ]);

  // Guided tour (/tour): the games arcade — featured quiz, the catalogue, the filter.
  useAssistantTour({
    label: intl.formatMessage({
      id: "grow.play.tour.label",
      defaultMessage: "Grow Play",
    }),
    summary: intl.formatMessage(
      {
        id: "grow.play.tour.summary",
        defaultMessage:
          "This is the Play arcade: {count} playful games and exercises that get your powers moving, solo or as a team.",
      },
      { count: GAMES.length },
    ),
    stops: [
      {
        anchor: "grow.startQuiz",
        title: intl.formatMessage({
          id: "grow.play.tour.quiz.title",
          defaultMessage: "The Power Quiz",
        }),
        description: intl.formatMessage({
          id: "grow.play.tour.quiz.desc",
          defaultMessage:
            "The featured game: 90 statements across all 30 powers on a 5-point scale. Your answers build your power profile. Click here to start.",
        }),
      },
      {
        anchor: "grow.play.grid",
        title: intl.formatMessage({
          id: "grow.play.tour.grid.title",
          defaultMessage: "All games",
        }),
        description: intl.formatMessage(
          {
            id: "grow.play.tour.grid.desc",
            defaultMessage:
              "The rest of the catalogue holds {count} more games like scenarios, card games and team exercises. Cards with a play button are ready to start.",
          },
          { count: GAMES.filter((g) => !g.featured).length },
        ),
      },
      {
        anchor: "grow.play.filter",
        title: intl.formatMessage({
          id: "grow.play.tour.filter.title",
          defaultMessage: "Solo or team",
        }),
        description: intl.formatMessage({
          id: "grow.play.tour.filter.desc",
          defaultMessage:
            "Filter the catalogue: Solo shows games you can play alone, Team the ones for groups.",
        }),
      },
    ],
  });

  return (
    <div className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="mb-5 flex items-baseline justify-between">
        <BackButton />
        <div className="text-[10px] tracking-[0.25em] text-ink/55">
          <FormattedMessage
            id="grow.play.headerCount"
            defaultMessage="{count} games · solo & team"
            values={{ count: GAMES.length }}
          />
        </div>
      </div>

      <h1 className="font-serif text-5xl leading-tight">
        <FormattedMessage id="grow.play.title" defaultMessage="Play" />
      </h1>
      <p className="mt-3 max-w-xl text-sm text-ink/55">
        <FormattedMessage
          id="grow.play.intro"
          defaultMessage="Playful exercises, quizzes and scenarios that get your powers moving. Solo, with a partner or as a team."
        />
      </p>

      <div className="mt-6 flex flex-wrap gap-2" data-assistant-anchor="grow.play.filter">
        {FILTERS.map((f) => (
          <FilterChip
            key={f}
            active={f === filter}
            onClick={() => setFilter(f)}
          >
            <FormattedMessage {...FILTER_LABELS[f]} />
          </FilterChip>
        ))}
      </div>

      {filter === "Alle" && featured ? (
        <div className="mt-8">
          <div className="mb-3 text-[10px] tracking-[0.25em] text-ink/45">
            <FormattedMessage id="grow.play.featured" defaultMessage="Featured" />
          </div>
          <FeaturedGame
            game={featured}
            intl={intl}
            onClick={() => featured.target && navigate(featured.target)}
          />
        </div>
      ) : null}

      <div className="mt-8">
        <div className="mb-3 text-[10px] tracking-[0.25em] text-ink/45">
          <FormattedMessage id="grow.play.allGames" defaultMessage="All games" />
        </div>
        <div
          className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4"
          data-assistant-anchor="grow.play.grid"
        >
          {rest.map((g) => (
            <GameCard
              key={g.id}
              game={g}
              intl={intl}
              onClick={() => g.target && navigate(g.target)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FeaturedGame({
  game,
  intl,
  onClick,
}: {
  game: GameItem;
  intl: IntlShape;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      // FeaturedGame only ever renders the power quiz, so the Assistant highlight anchor
      // (HighlightElement tool) can sit directly on this button.
      data-assistant-anchor="grow.startQuiz"
      className="grid w-full grid-cols-1 overflow-hidden rounded-md bg-surface text-left ring-1 ring-ink/5 transition hover:ring-accent/40 @lg:grid-cols-[minmax(240px,0.75fr)_minmax(0,1.25fr)]"
    >
      <div className="relative">
        {game.collage ? (
          <PowerCollage className="h-full min-h-[200px]" />
        ) : game.imageSrc ? (
          <BlurredArtwork src={game.imageSrc} className="h-full min-h-[200px]" />
        ) : (
          <AbstractTile variant={game.variant} hue={game.hue} className="h-full min-h-[200px]" />
        )}
        <span className="absolute left-3 top-3 rounded bg-accent px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-on-accent">
          {intl.formatMessage({ id: game.kindId, defaultMessage: game.kindDefault })}
        </span>
      </div>
      <div className="flex flex-col justify-between p-6">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-accent">
            <FormattedMessage id="grow.play.featured" defaultMessage="Featured" />
          </div>
          <h2 className="mt-2 font-serif text-3xl">{game.title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-ink/70">
            {intl.formatMessage({ id: game.captionId, defaultMessage: game.captionDefault })}
          </p>
        </div>
        <div className="mt-5 flex items-center gap-4 text-[10px] tracking-wider text-ink/55">
          <span>● {intl.formatMessage({ id: game.durationId, defaultMessage: game.durationDefault })}</span>
          <span>
            ● {game.players}{" "}
            {game.players === "1" ? (
              <FormattedMessage id="grow.play.personSingular" defaultMessage="person" />
            ) : (
              <FormattedMessage id="grow.play.personPlural" defaultMessage="people" />
            )}
          </span>
          <span className="ml-auto text-accent">
            <FormattedMessage id="grow.play.start" defaultMessage="Start ↗" />
          </span>
        </div>
      </div>
    </button>
  );
}

function GameCard({
  game,
  intl,
  onClick,
}: {
  game: GameItem;
  intl: IntlShape;
  onClick: () => void;
}) {
  const playable = Boolean(game.target);
  return (
    <button
      onClick={onClick}
      className="overflow-hidden rounded-md bg-surface text-left ring-1 ring-ink/5 transition hover:ring-accent/40"
    >
      <div className="relative">
        {game.collage ? (
          <PowerCollage className="aspect-[16/10]" />
        ) : game.imageSrc ? (
          <BlurredArtwork src={game.imageSrc} className="aspect-[16/10]" />
        ) : (
          <AbstractTile variant={game.variant} hue={game.hue} className="aspect-[16/10]" />
        )}
        <span className="absolute left-2 top-2 rounded bg-ink px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-on-ink">
          {intl.formatMessage({ id: game.kindId, defaultMessage: game.kindDefault })}
        </span>
        {playable ? (
          <span className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-accent text-on-accent">
            <PlayIcon />
          </span>
        ) : null}
      </div>
      <div className="px-4 py-3">
        <div className="text-sm font-medium leading-tight">{game.title}</div>
        <div className="mt-1.5 text-[11px] leading-snug text-ink/55">
          {intl.formatMessage({ id: game.captionId, defaultMessage: game.captionDefault })}
        </div>
        <div className="mt-3 flex items-center justify-between text-[10px] tracking-wider text-ink/40">
          <span>{intl.formatMessage({ id: game.durationId, defaultMessage: game.durationDefault })}</span>
          <span>
            {game.players === "1" ? (
              <FormattedMessage id="grow.play.filter.solo" defaultMessage="Solo" />
            ) : (
              <FormattedMessage
                id="grow.play.teamCount"
                defaultMessage="Team · {count}"
                values={{ count: game.players }}
              />
            )}
          </span>
        </div>
      </div>
    </button>
  );
}

// Same blur+scrim idiom as the PowerTile hover reveal, but without the
// reveal: the card artwork stays blurred as an ambient teaser.
function BlurredArtwork({ src, className }: { src: string; className?: string }) {
  return (
    <div className={`relative overflow-hidden ${className ?? ""}`} aria-hidden>
      <img
        src={src}
        alt=""
        className="absolute inset-0 h-full w-full scale-110 object-cover blur-md"
      />
      <div className="absolute inset-0 bg-cream/35" />
    </div>
  );
}

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
